import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Assessment as DashboardIcon,
  InfoOutlined as InfoIcon,
  Refresh as RefreshIcon,
  FlagOutlined as TargetIcon,
  Inventory2Outlined as ReceivedIcon,
  HourglassEmptyOutlined as AwaitingIcon,
  ReceiptLongOutlined as OrdersIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import PageHeader from '../components/PageHeader';
import { SetupCoachmark } from '../components/selfSetup/SetupCoachmark';
import { useSelfSetupTour } from '../hooks/useSelfSetupTour';
import { useOrganizationStore } from '../stores/organization.store';
import { OrganizationType } from '../types/organization.type';
import { getRecyclerAppetiteFulfillment } from '../services/organization.service';
import { getRecyclerPreOrdersOverview } from '../services/recycler-dashboard.service';
import { formatLocalizedDate } from '../helpers/date-locale.helper';
import {
  groupFulfillmentLines,
  lineDisplayName,
  sumTargeted,
} from '../helpers/fulfillment-groups.helper';
import type {
  RecyclerAppetiteFulfillmentLine,
  RecyclerAppetiteFulfillmentPeriod,
  RecyclerAppetiteFulfillmentResponse,
} from '../types/recycler-appetite.type';
import { usePreOrderStore } from '../stores/pre-order.store';
import { PreOrderStatus, type PreOrderSchema } from '../types/pre-order.type';
import { HELP_GUIDE_DASHBOARD } from '../constants/knowledgeBaseTryIt';
import {
  clearHelpGuideActive,
  markHelpGuideActive,
} from '../utils/helpGuideSession';

interface GenericResponse<T> {
  result: T;
}

type PeriodTab = 'month' | 'week';

type FulfillmentDisplay = {
  barPercent: number | null;
  statusText: string;
  showBar: boolean;
};

const SURFACE_RADIUS = '8px';
const ORDER_PREVIEW_LIMIT = 5;

function formatKg(value: number, locale: string): string {
  return value.toLocaleString(locale, { maximumFractionDigits: 1 });
}

function formatMass(value: number, locale: string, unit: string): string {
  return `${formatKg(value, locale)} ${unit}`;
}

function fulfillmentPercent(line: RecyclerAppetiteFulfillmentLine): number {
  if (line.fulfillment_ratio != null) {
    return Math.round(line.fulfillment_ratio * 100);
  }
  if (line.target_kg <= 0) return 0;
  return Math.round((line.received_kg / line.target_kg) * 100);
}

function getFulfillmentDisplay(line: RecyclerAppetiteFulfillmentLine, t: TFunction): FulfillmentDisplay {
  if (line.target_kg <= 0) {
    if (line.received_kg > 0) {
      return {
        barPercent: null,
        statusText: t('dashboard.receivedWithoutTarget'),
        showBar: false,
      };
    }
    return {
      barPercent: 0,
      statusText: t('dashboard.noTargetSet'),
      showBar: true,
    };
  }

  const pct = fulfillmentPercent(line);
  return {
    barPercent: Math.min(pct, 100),
    statusText: `${pct}%`,
    showBar: true,
  };
}

function getOverallFulfillmentSummary(
  targetKg: number,
  receivedKg: number,
  t: TFunction,
): { label: string; value: string } {
  if (targetKg <= 0) {
    if (receivedKg > 0) {
      return {
        label: t('dashboard.fulfillment'),
        value: t('dashboard.receivedWithoutTargetShort'),
      };
    }
    return {
      label: t('dashboard.fulfillment'),
      value: t('dashboard.noTargetSet'),
    };
  }
  const pct = Math.round((receivedKg / targetKg) * 100);
  return {
    label: t('dashboard.fulfillment'),
    value: `${pct}%`,
  };
}

function progressColor(
  line: RecyclerAppetiteFulfillmentLine,
  acceptsExtra: boolean,
): 'primary' | 'success' | 'warning' | 'error' | 'info' {
  if (line.is_over_target && !acceptsExtra && line.is_excess_disallowed) return 'error';
  if (line.is_over_target) return 'warning';
  if (line.target_kg <= 0 && line.received_kg > 0) return 'info';
  const pct = fulfillmentPercent(line);
  if (pct >= 100) return 'success';
  return 'primary';
}

function getOverallFulfillmentPercent(targetKg: number, receivedKg: number): number | null {
  if (targetKg <= 0) return receivedKg > 0 ? null : 0;
  return Math.round((receivedKg / targetKg) * 100);
}

function overallGaugeColor(
  percent: number | null,
  receivedKg: number,
  targetKg: number,
): 'primary' | 'success' | 'warning' | 'error' | 'info' {
  if (targetKg <= 0) return receivedKg > 0 ? 'info' : 'primary';
  if (percent == null) return 'info';
  if (percent > 100) return 'warning';
  if (percent >= 80) return 'success';
  return 'primary';
}

function formatOrderStatus(status: string): string {
  return status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function orderStatusColor(status: string): 'default' | 'warning' | 'info' | 'success' | 'error' {
  if (status === PreOrderStatus.PENDING) return 'warning';
  if (status === PreOrderStatus.PARTIALLY_FULFILLED) return 'info';
  if (status === PreOrderStatus.COMPLETED) return 'success';
  if (status === PreOrderStatus.CANCELLED) return 'error';
  return 'default';
}

function orderMaterialLabel(order: PreOrderSchema, t: TFunction): string {
  const name = order.material_name_snapshot?.trim();
  if (name) return name;
  return t(`categories.${order.material_category}`, {
    defaultValue: formatOrderStatus(order.material_category),
  });
}

function overTargetLabel(line: RecyclerAppetiteFulfillmentLine, acceptsExtra: boolean, t: TFunction): string {
  return !acceptsExtra && line.is_excess_disallowed
    ? t('dashboard.excessNotAllowed')
    : t('dashboard.overTarget');
}

function KpiStatCard({
  label,
  value,
  icon,
  accent,
}: {
  label: string;
  value: string;
  icon: React.ReactNode;
  accent: string;
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.75,
        height: '100%',
        borderRadius: SURFACE_RADIUS,
        border: 1,
        borderColor: 'divider',
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="center">
        <Box sx={{ color: accent, display: 'flex' }}>{icon}</Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {label}
          </Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
            {value}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
}

function FulfillmentGauge({
  label,
  displayValue,
  percent,
  color,
  hint,
  awaitingDeliveries,
}: {
  label: string;
  displayValue: string;
  percent: number | null;
  color: 'primary' | 'success' | 'warning' | 'error' | 'info';
  hint?: string;
  awaitingDeliveries?: boolean;
}) {
  const ringValue = percent != null ? Math.min(percent, 100) : 0;
  const gauge = (
    <Paper
      elevation={0}
      sx={{
        p: 1.75,
        height: '100%',
        borderRadius: SURFACE_RADIUS,
        border: 1,
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 2,
      }}
    >
      <Box sx={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
        <CircularProgress
          variant="determinate"
          value={awaitingDeliveries ? 100 : ringValue}
          size={64}
          thickness={4}
          color={awaitingDeliveries ? undefined : color}
          sx={awaitingDeliveries ? { color: 'action.disabledBackground' } : undefined}
        />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {awaitingDeliveries ? (
            <AwaitingIcon sx={{ fontSize: 24, color: 'text.disabled' }} />
          ) : (
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {displayValue}
            </Typography>
          )}
        </Box>
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {label}
        </Typography>
        {hint && (
          <Typography
            variant={awaitingDeliveries ? 'body2' : 'caption'}
            color="text.secondary"
            sx={{ display: 'block', mt: 0.25, fontWeight: awaitingDeliveries ? 600 : 400 }}
          >
            {hint}
          </Typography>
        )}
      </Box>
    </Paper>
  );

  if (!awaitingDeliveries) return gauge;
  return (
    <Tooltip title={hint ?? ''} arrow placement="top">
      {gauge}
    </Tooltip>
  );
}

function FulfillmentProgress({
  line,
  acceptsExtra,
  compact,
}: {
  line: RecyclerAppetiteFulfillmentLine;
  acceptsExtra: boolean;
  compact?: boolean;
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const display = getFulfillmentDisplay(line, t);
  const color = progressColor(line, acceptsExtra);

  if (!display.showBar) {
    return (
      <Typography variant="caption" color="info.main" sx={{ fontWeight: 600 }}>
        {display.statusText}
      </Typography>
    );
  }

  return (
    <Stack spacing={0.75}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <LinearProgress
          variant="determinate"
          value={display.barPercent ?? 0}
          color={color}
          sx={{
            flex: 1,
            height: 8,
            borderRadius: 4,
            bgcolor: alpha(theme.palette.divider, 0.6),
          }}
        />
        <Typography variant="caption" sx={{ minWidth: 40, textAlign: 'right', fontWeight: 600, flexShrink: 0, fontSize: '0.875rem' }}>
          {display.statusText}
        </Typography>
      </Stack>
      {line.is_over_target && (
        <Chip
          size="small"
          color={!acceptsExtra && line.is_excess_disallowed ? 'error' : 'warning'}
          label={overTargetLabel(line, acceptsExtra, t)}
          sx={{
            alignSelf: compact ? 'flex-start' : 'flex-start',
            maxWidth: '100%',
            height: 22,
            '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem' },
          }}
        />
      )}
    </Stack>
  );
}

function FulfillmentCards({
  lines,
  acceptsExtra,
  locale,
  unit,
}: {
  lines: RecyclerAppetiteFulfillmentLine[];
  acceptsExtra: boolean;
  locale: string;
  unit: string;
}) {
  const { t } = useTranslation();

  return (
    <Stack spacing={1.5} sx={{ p: 0 }}>
      {lines.map((line) => (
        <Paper
          key={line.material_id}
          elevation={0}
          sx={{ p: 1.5, borderRadius: SURFACE_RADIUS, border: 1, borderColor: 'divider' }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            {lineDisplayName(line)}
          </Typography>
          <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {t('dashboard.targetShort')}: {formatMass(line.target_kg, locale, unit)}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontWeight: line.is_over_target ? 600 : 400,
                color: line.is_over_target ? 'warning.main' : 'text.primary',
              }}
            >
              {t('dashboard.receivedShort')}: {formatMass(line.received_kg, locale, unit)}
            </Typography>
          </Stack>
          <FulfillmentProgress line={line} acceptsExtra={acceptsExtra} compact />
        </Paper>
      ))}
    </Stack>
  );
}

function FulfillmentTable({
  lines,
  acceptsExtra,
  locale,
  unit,
}: {
  lines: RecyclerAppetiteFulfillmentLine[];
  acceptsExtra: boolean;
  locale: string;
  unit: string;
}) {
  const { t } = useTranslation();

  if (lines.length === 0) {
    return (
      <Box sx={{ py: 5, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {t('dashboard.noLinesInPeriod')}
        </Typography>
      </Box>
    );
  }

  const cellSx = { py: 1.75, px: 3 };

  return (
    <TableContainer sx={{ width: '100%', overflowX: 'auto' }}>
      <Table sx={{ width: '100%' }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: '34%', fontWeight: 600, py: 2, px: 3, fontSize: '0.9375rem' }}>
              {t('dashboard.wasteType')}
            </TableCell>
            <TableCell align="right" sx={{ width: '15%', fontWeight: 600, py: 2, px: 3, fontSize: '0.9375rem' }}>
              {t('dashboard.targetShort')}
            </TableCell>
            <TableCell align="right" sx={{ width: '15%', fontWeight: 600, py: 2, px: 3, fontSize: '0.9375rem' }}>
              {t('dashboard.receivedShort')}
            </TableCell>
            <TableCell sx={{ width: '36%', fontWeight: 600, py: 2, px: 3, fontSize: '0.9375rem' }}>
              {t('dashboard.fulfillment')}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {lines.map((line) => (
            <TableRow key={line.material_id} hover>
              <TableCell sx={cellSx}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {lineDisplayName(line)}
                </Typography>
              </TableCell>
              <TableCell align="right" sx={cellSx}>
                <Typography variant="body2" color="text.secondary">
                  {formatMass(line.target_kg, locale, unit)}
                </Typography>
              </TableCell>
              <TableCell align="right" sx={cellSx}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: line.is_over_target ? 600 : 400,
                    color: line.is_over_target ? 'warning.main' : 'text.primary',
                  }}
                >
                  {formatMass(line.received_kg, locale, unit)}
                </Typography>
              </TableCell>
              <TableCell sx={cellSx}>
                <FulfillmentProgress line={line} acceptsExtra={acceptsExtra} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function formatPeriodRange(
  period: RecyclerAppetiteFulfillmentPeriod | undefined,
  language: string,
): { start: string; end: string } | null {
  if (!period?.period_start || !period?.period_end_exclusive) return null;
  return {
    start: formatLocalizedDate(parseISO(period.period_start), language),
    end: formatLocalizedDate(parseISO(period.period_end_exclusive), language),
  };
}

const AppetiteDashboard: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const periodFromQuery = searchParams.get('period');
  const [periodTab, setPeriodTab] = useState<PeriodTab>(() =>
    periodFromQuery === 'week' ? 'week' : 'month',
  );
  useEffect(() => {
    if (periodFromQuery === 'week' || periodFromQuery === 'month') {
      setPeriodTab(periodFromQuery);
    }
  }, [periodFromQuery]);
  const {
    isStep,
    tourPhase,
    setTourPhase,
    advanceTourPhase,
    completeStep,
    dismissTour,
  } = useSelfSetupTour();
  const [helpDashboardGuide, setHelpDashboardGuide] = useState(false);
  const [helpDashboardPhase, setHelpDashboardPhase] = useState<
    'periodToggle' | 'fulfillment' | 'fulfillmentTable' | 'dashboardOrders'
  >('periodToggle');

  const showDashboardTour =
    isStep('dashboard') && tourPhase !== 'nav' && !helpDashboardGuide;
  const showPeriodTip =
    (showDashboardTour && tourPhase === 'periodToggle') ||
    (helpDashboardGuide && helpDashboardPhase === 'periodToggle');
  const showFulfillmentTip =
    (showDashboardTour && tourPhase === 'fulfillment') ||
    (helpDashboardGuide && helpDashboardPhase === 'fulfillment');
  const showFulfillmentTableTip =
    (showDashboardTour && tourPhase === 'fulfillmentTable') ||
    (helpDashboardGuide && helpDashboardPhase === 'fulfillmentTable');
  const showDashboardOrdersTip =
    (showDashboardTour && tourPhase === 'dashboardOrders') ||
    (helpDashboardGuide && helpDashboardPhase === 'dashboardOrders');

  const finishHelpDashboardGuide = useCallback(() => {
    setHelpDashboardGuide(false);
    setHelpDashboardPhase('periodToggle');
    clearHelpGuideActive();
  }, []);

  const softDismissDashboardTour = () => {
    if (helpDashboardGuide) {
      finishHelpDashboardGuide();
      return;
    }
    dismissTour();
  };

  useEffect(() => {
    const guide = searchParams.get('helpGuide');
    if (guide !== HELP_GUIDE_DASHBOARD) return;
    markHelpGuideActive(guide);
    setHelpDashboardGuide(true);
    setHelpDashboardPhase('periodToggle');
    const next = new URLSearchParams(searchParams);
    next.delete('helpGuide');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const selectedOrganization = useOrganizationStore((s) => s.selectedOrganization);
  const {
    preOrders,
    preOrdersLoading,
    totalPreOrders,
    fetchPreOrders,
  } = usePreOrderStore();
  const orgId = selectedOrganization?.id;
  const isRecycler = selectedOrganization?.organization_type === OrganizationType.RECYCLER;

  const dateLocale = i18n.language;

  const { data, isLoading, isError, isFetching, refetch } = useQuery({
    queryKey: ['recycler-appetite-fulfillment', orgId],
    queryFn: async () => {
      const res = (await getRecyclerAppetiteFulfillment(orgId!)) as GenericResponse<RecyclerAppetiteFulfillmentResponse>;
      return res.result;
    },
    enabled: Boolean(orgId && isRecycler),
  });

  // Whole-history order totals in kg. Falls back to the preview rows when the
  // dashboard endpoint is not permitted for this user.
  const ordersOverview = useQuery({
    queryKey: ['recycler-pre-orders-overview', orgId],
    queryFn: () => getRecyclerPreOrdersOverview(orgId!),
    enabled: Boolean(orgId && isRecycler),
    retry: false,
  });

  const configRepeat = data?.repeat === 'monthly' ? 'monthly' : 'weekly';
  const acceptsExtra = data?.accepts_additional_quantity !== false;
  const week = data?.this_week;
  const month = data?.this_month;
  const weekLines = week?.lines ?? [];
  const monthLines = month?.lines ?? [];
  const fullyEmpty = !isLoading && !isError && weekLines.length === 0 && monthLines.length === 0;

  const advanceDashboardTip = () => {
    if (helpDashboardGuide) {
      if (helpDashboardPhase === 'periodToggle') {
        // New orgs often have no fulfillment rows yet: skip data tips.
        if (fullyEmpty) {
          setHelpDashboardPhase('dashboardOrders');
          return;
        }
        setHelpDashboardPhase('fulfillment');
        return;
      }
      if (helpDashboardPhase === 'fulfillment') {
        setHelpDashboardPhase('fulfillmentTable');
        return;
      }
      if (helpDashboardPhase === 'fulfillmentTable') {
        setHelpDashboardPhase('dashboardOrders');
        return;
      }
      finishHelpDashboardGuide();
      return;
    }
    if (tourPhase === 'periodToggle' && fullyEmpty) {
      setTourPhase('dashboardOrders');
      return;
    }
    if (tourPhase === 'dashboardOrders') {
      completeStep('dashboard');
      return;
    }
    advanceTourPhase();
  };

  // If the tour landed on data-only tips while the dashboard is empty, jump ahead.
  useEffect(() => {
    if (!fullyEmpty) return;
    const stuckHelp =
      helpDashboardGuide &&
      (helpDashboardPhase === 'fulfillment' || helpDashboardPhase === 'fulfillmentTable');
    const stuckTour =
      showDashboardTour &&
      (tourPhase === 'fulfillment' || tourPhase === 'fulfillmentTable');
    if (stuckHelp) {
      setHelpDashboardPhase('dashboardOrders');
      return;
    }
    if (stuckTour) {
      setTourPhase('dashboardOrders');
    }
  }, [
    fullyEmpty,
    helpDashboardGuide,
    helpDashboardPhase,
    showDashboardTour,
    tourPhase,
    setTourPhase,
  ]);

  const activePeriod = periodTab === 'month' ? month : week;
  const activeLines = periodTab === 'month' ? monthLines : weekLines;
  const activePeriodRange = formatPeriodRange(activePeriod, dateLocale);
  const groups = useMemo(() => groupFulfillmentLines(activeLines), [activeLines]);
  // Only waste types that have a supply target are shown, one row each; mass
  // received for a waste type with no target is deliberately left off this page.
  const displayLines = useMemo(() => groups.targeted.flatMap((group) => group.lines), [groups]);
  const activeTotals = useMemo(() => sumTargeted(groups), [groups]);
  const overallSummary = useMemo(
    () => getOverallFulfillmentSummary(activeTotals.targetKg, activeTotals.receivedKg, t),
    [activeTotals, t],
  );
  const overallPercent = useMemo(
    () => getOverallFulfillmentPercent(activeTotals.targetKg, activeTotals.receivedKg),
    [activeTotals],
  );
  const gaugeColor = overallGaugeColor(
    overallPercent,
    activeTotals.receivedKg,
    activeTotals.targetKg,
  );
  const massUnit = t('units.kg');
  const viewLabel = periodTab === 'month' ? t('dashboard.monthTitle') : t('dashboard.weekAnalysisTitle');
  const supplyConfigHint =
    configRepeat === 'monthly'
      ? t('dashboard.supplyConfigHintMonthly')
      : t('dashboard.supplyConfigHintWeekly');
  const supplyConfigDiffersFromView =
    (periodTab === 'month' && configRepeat === 'weekly') ||
    (periodTab === 'week' && configRepeat === 'monthly');
  const periodInfoTooltip = supplyConfigDiffersFromView ? (
    <>
      {supplyConfigHint}
      <br />
      <br />
      {t('dashboard.utcHelp')}
    </>
  ) : (
    t('dashboard.utcHelp')
  );
  const gaugeHint =
    activeTotals.receivedKg === 0 && activeTotals.targetKg > 0
      ? t('dashboard.noDeliveriesYet')
      : undefined;

  useEffect(() => {
    if (!orgId || !isRecycler) return;
    void fetchPreOrders({ organization_id: orgId, page: 1, limit: ORDER_PREVIEW_LIMIT });
  }, [fetchPreOrders, orgId, isRecycler]);

  const orderSummary = useMemo(() => {
    const overview = ordersOverview.data;
    if (overview) {
      return {
        fromOverview: true,
        total: overview.total_orders,
        pending: overview.pending_count,
        partial: overview.partially_fulfilled_count,
        remainingKg: overview.remaining_kg,
      };
    }
    const remainingKg = preOrders.reduce(
      (acc, order) => acc + Math.max(0, (order.amount_kg ?? 0) - (order.fulfilled_kg ?? 0)),
      0,
    );
    return {
      fromOverview: false,
      total: totalPreOrders || preOrders.length,
      pending: preOrders.filter((order) => order.status === PreOrderStatus.PENDING).length,
      partial: preOrders.filter((order) => order.status === PreOrderStatus.PARTIALLY_FULFILLED).length,
      remainingKg,
    };
  }, [ordersOverview.data, preOrders, totalPreOrders]);

  if (!isRecycler) return null;

  const periodToggleSx = {
    gap: 1,
    '& .MuiToggleButtonGroup-grouped:not(:first-of-type)': {
      marginLeft: 0,
    },
    '& .MuiToggleButton-root': {
      px: 1.75,
      py: 0.5,
      textTransform: 'none',
      fontWeight: 600,
      fontSize: '0.8125rem',
      borderRadius: `${SURFACE_RADIUS} !important`,
      border: 1,
      borderColor: 'divider',
      '&:hover': {
        bgcolor: alpha(theme.palette.success.main, 0.1),
        borderColor: alpha(theme.palette.success.main, 0.45),
        color: theme.palette.success.main,
      },
      '&.Mui-selected': {
        bgcolor: alpha(theme.palette.success.main, 0.14),
        borderColor: theme.palette.success.main,
        color: theme.palette.success.main,
        '&:hover': {
          bgcolor: alpha(theme.palette.success.main, 0.2),
        },
      },
    },
  };

  return (
    <Box sx={{ width: '100%' }}>
      <PageHeader
        icon={<DashboardIcon />}
        title={t('dashboard.title')}
        subtitle={t('dashboard.subtitle')}
        action={
          <Tooltip title={t('common.refresh')}>
            <span>
              <IconButton
                onClick={() => {
                  void refetch();
                  void ordersOverview.refetch();
                  if (orgId) void fetchPreOrders({ organization_id: orgId, page: 1, limit: ORDER_PREVIEW_LIMIT });
                }}
                disabled={isFetching || preOrdersLoading}
                size="small"
              >
                {isFetching ? <CircularProgress size={18} /> : <RefreshIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        }
      />

      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('dashboard.loadError')}
        </Alert>
      )}

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {fullyEmpty && (
        <SetupCoachmark
          open={showPeriodTip}
          title={t('selfSetup.tour.dashboardPeriodTitle')}
          subtitle={t('dashboard.emptyBody')}
          onDismiss={softDismissDashboardTour}
          onNext={advanceDashboardTip}
          nextLabel={t('selfSetup.tourNext')}
          fullWidth
        >
          <Paper
            id="self-setup-dashboard-period"
            elevation={0}
            sx={{
              p: 4,
              borderRadius: SURFACE_RADIUS,
              border: 1,
              borderColor: 'divider',
              textAlign: 'center',
            }}
          >
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              {t('dashboard.emptyBody')}
            </Typography>
            <Button component={RouterLink} to="/supply" variant="contained">
              {t('dashboard.goToSupply')}
            </Button>
          </Paper>
        </SetupCoachmark>
      )}

      {!isLoading && !isError && !fullyEmpty && (
        <Paper
          elevation={0}
          sx={{ borderRadius: SURFACE_RADIUS, border: 1, borderColor: 'divider', overflow: 'hidden' }}
        >
          <Box sx={{ px: { xs: 2, sm: 2.5 }, py: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Stack spacing={1.5}>
              <SetupCoachmark
                open={showPeriodTip}
                title={t('selfSetup.tour.dashboardPeriodTitle')}
                subtitle={t('selfSetup.tour.dashboardPeriodSubtitle')}
                onDismiss={softDismissDashboardTour}
                onNext={advanceDashboardTip}
                nextLabel={t('selfSetup.tourNext')}
              >
                <ToggleButtonGroup
                  id="self-setup-dashboard-period"
                  exclusive
                  size="small"
                  value={periodTab}
                  onChange={(_, value: PeriodTab | null) => {
                    if (value) setPeriodTab(value);
                  }}
                  sx={periodToggleSx}
                >
                  <ToggleButton value="month">{t('dashboard.monthTitle')}</ToggleButton>
                  <ToggleButton value="week">{t('dashboard.weekAnalysisTitle')}</ToggleButton>
                </ToggleButtonGroup>
              </SetupCoachmark>

              <Stack direction="row" alignItems="center" gap={0.5}>
                {activePeriodRange && (
                  <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 0 }}>
                    {t('dashboard.periodHeader', { view: viewLabel, ...activePeriodRange })}
                  </Typography>
                )}
                <Tooltip title={periodInfoTooltip} arrow placement="top">
                  <IconButton
                    size="small"
                    aria-label={t('dashboard.utcHelpTitle')}
                    sx={{ color: 'text.disabled', flexShrink: 0 }}
                  >
                    <InfoIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>
          </Box>

          <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
            <SetupCoachmark
              open={showFulfillmentTip}
              title={t('selfSetup.tour.dashboardFulfillmentTitle')}
              subtitle={t('selfSetup.tour.dashboardFulfillmentSubtitle')}
              onDismiss={softDismissDashboardTour}
              onNext={advanceDashboardTip}
              nextLabel={t('selfSetup.tourNext')}
              fullWidth
            >
            <Grid container spacing={1.5} sx={{ mb: 2 }} id="self-setup-dashboard-fulfillment">
              <Grid item xs={12} sm={6} md={4}>
                <KpiStatCard
                  label={t('dashboard.targetKg')}
                  value={formatMass(activeTotals.targetKg, i18n.language, massUnit)}
                  icon={<TargetIcon fontSize="small" />}
                  accent={theme.palette.primary.main}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <KpiStatCard
                  label={t('dashboard.receivedKg')}
                  value={formatMass(activeTotals.receivedKg, i18n.language, massUnit)}
                  icon={<ReceivedIcon fontSize="small" />}
                  accent={theme.palette.success.main}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FulfillmentGauge
                  label={overallSummary.label}
                  displayValue={overallSummary.value}
                  percent={overallPercent}
                  color={gaugeColor}
                  hint={gaugeHint}
                  awaitingDeliveries={Boolean(gaugeHint)}
                />
              </Grid>
            </Grid>
            </SetupCoachmark>

            <SetupCoachmark
              open={showFulfillmentTableTip}
              title={t('selfSetup.tour.dashboardTableTitle')}
              subtitle={t('selfSetup.tour.dashboardTableSubtitle')}
              onDismiss={softDismissDashboardTour}
              onNext={advanceDashboardTip}
              nextLabel={t('selfSetup.tourNext')}
              fullWidth
            >
              <Box id="self-setup-dashboard-table">
            {displayLines.length === 0 ? (
              <Box
                sx={{
                  py: 6,
                  px: 2,
                  textAlign: 'center',
                  minHeight: 360,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: SURFACE_RADIUS,
                }}
              >
                <Typography variant="body1" color="text.secondary">
                  {t('dashboard.noLinesInPeriod')}
                </Typography>
              </Box>
            ) : isMobile ? (
              <FulfillmentCards
                lines={displayLines}
                acceptsExtra={acceptsExtra}
                locale={i18n.language}
                unit={massUnit}
              />
            ) : (
              <Paper
                elevation={0}
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: SURFACE_RADIUS,
                  overflow: 'hidden',
                  width: '100%',
                }}
              >
                <FulfillmentTable
                  lines={displayLines}
                  acceptsExtra={acceptsExtra}
                  locale={i18n.language}
                  unit={massUnit}
                />
              </Paper>
            )}
              </Box>
            </SetupCoachmark>
          </Box>
        </Paper>
      )}

      <SetupCoachmark
        open={showDashboardOrdersTip}
        title={t('selfSetup.tour.dashboardOrdersTitle')}
        subtitle={t('selfSetup.tour.dashboardOrdersSubtitle')}
        onDismiss={softDismissDashboardTour}
        onNext={advanceDashboardTip}
        nextLabel={t('selfSetup.tourDone')}
        fullWidth
        placement="top"
      >
        <Paper
          id="self-setup-dashboard-orders"
          elevation={0}
          sx={{ mt: 2.5, borderRadius: SURFACE_RADIUS, border: 1, borderColor: 'divider', overflow: 'hidden' }}
        >
          <Box sx={{ px: { xs: 2, sm: 2.5 }, py: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              alignItems={{ sm: 'center' }}
              justifyContent="space-between"
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <OrdersIcon color="primary" fontSize="small" />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {t('orders.title')}
                </Typography>
              </Stack>
              <Button component={RouterLink} to="/orders" size="small" variant="outlined">
                {t('orders.manage')}
              </Button>
            </Stack>
          </Box>

          <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6} md={3}>
                <KpiStatCard
                  label={t('orders.totalOrders')}
                  value={String(orderSummary.total)}
                  icon={<OrdersIcon fontSize="small" />}
                  accent={theme.palette.primary.main}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <KpiStatCard
                  label={t('orders.pending')}
                  value={String(orderSummary.pending)}
                  icon={<AwaitingIcon fontSize="small" />}
                  accent={theme.palette.warning.main}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <KpiStatCard
                  label={t('orders.partiallyFulfilled')}
                  value={String(orderSummary.partial)}
                  icon={<ReceivedIcon fontSize="small" />}
                  accent={theme.palette.info.main}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <KpiStatCard
                  label={t('dashboard.remainingKg')}
                  value={formatKg(orderSummary.remainingKg, i18n.language)}
                  icon={<TargetIcon fontSize="small" />}
                  accent={theme.palette.success.main}
                />
              </Grid>
            </Grid>
            {!orderSummary.fromOverview && preOrders.length > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                {t('dashboard.ordersOverviewFallback', { count: preOrders.length })}
              </Typography>
            )}

            {preOrdersLoading ? (
              <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={24} />
              </Box>
            ) : preOrders.length === 0 ? (
              <Box sx={{ py: 4, textAlign: 'center', border: 1, borderColor: 'divider', borderRadius: SURFACE_RADIUS }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  {t('orders.emptyYet')}
                </Typography>
                <Button component={RouterLink} to="/orders" variant="contained" size="small">
                  {t('orders.create')}
                </Button>
              </Box>
            ) : (
              <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: SURFACE_RADIUS }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('orders.material')}</TableCell>
                      <TableCell align="right">{t('orders.amount')}</TableCell>
                      <TableCell align="right">{t('orders.fulfilled')}</TableCell>
                      <TableCell>{t('orders.status')}</TableCell>
                      <TableCell>{t('orders.created')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {preOrders.slice(0, ORDER_PREVIEW_LIMIT).map((order) => (
                      <TableRow key={order.id} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {orderMaterialLabel(order, t)}
                          </Typography>
                          {order.material_name_snapshot && (
                            <Typography variant="caption" color="text.secondary">
                              {t(`categories.${order.material_category}`, {
                                defaultValue: formatOrderStatus(order.material_category),
                              })}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {Number(order.amount).toLocaleString(i18n.language, { maximumFractionDigits: 2 })}{' '}
                          {order.amount_unit
                            ? t(`units.${order.amount_unit}`, { defaultValue: String(order.amount_unit) })
                            : ''}
                        </TableCell>
                        <TableCell align="right">
                          {typeof order.fulfillment_percent === 'number' ? `${Math.round(order.fulfillment_percent)}%` : '—'}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={t(`orderStatus.${order.status}`, {
                              defaultValue: formatOrderStatus(order.status),
                            })}
                            color={orderStatusColor(order.status)}
                          />
                        </TableCell>
                        <TableCell>{formatLocalizedDate(new Date(order.created_at), i18n.language)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </Paper>
      </SetupCoachmark>
    </Box>
  );
};

export default AppetiteDashboard;
